import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, Fragment } from "react";
import {
  Paper,
  Title,
  Table,
  Group,
  Button,
  Text,
  Stack,
  Box,
  Alert,
  Center,
  Badge,
  Anchor,
  Loader,
} from "@mantine/core";
import { StatusBadge, PageLoader } from "@/components/common";
import { scrapesApi } from "@/api/client";
import type { ScrapeExecutionDetail } from "@/api/types";

const statusFilters = [
  { value: undefined, label: "All" },
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },
  { value: "running", label: "Running" },
  { value: "partial_failure", label: "Partial" },
] as const;

export const Route = createFileRoute("/_authenticated/scrapes")({
  validateSearch: (search: Record<string, unknown>) => ({
    status: search.status as string | undefined,
    page: Number(search.page) || 1,
  }),
  component: ScrapesPage,
});

function ScrapeDetailRow({ scrapeId }: { scrapeId: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["scrapes", scrapeId, "detail"],
    queryFn: () => scrapesApi.get(scrapeId),
  });

  if (isLoading) {
    return (
      <Center p="md">
        <Loader size="sm" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" m="md">
        Failed to load details
      </Alert>
    );
  }

  if (!data) return null;

  return (
    <Stack gap="md" p="md">
      {/* Timing & stats summary */}
      <Group gap="xl">
        {data.search_duration_s != null && (
          <Text size="sm" c="dimmed">
            Search: {data.search_duration_s}s
          </Text>
        )}
        <Text size="sm" c="dimmed">
          Rejected: {data.rejected_count}
        </Text>
        <Text size="sm" c="dimmed">
          Removed: {data.removed_count}
        </Text>
      </Group>

      {/* Error section */}
      {data.error_message && (
        <Alert color="red" title="Error">
          <Text size="sm" mb="sm">
            {data.error_message}
          </Text>
          {data.error_traceback && (
            <Box
              component="pre"
              style={{
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                overflowX: "auto",
              }}
            >
              {data.error_traceback}
            </Box>
          )}
        </Alert>
      )}

      {/* Per-filter breakdown */}
      {data.filters_detail.length > 0 && <FilterBreakdownTable data={data} />}

      {/* Apartments found */}
      {data.apartments.length > 0 && <ApartmentsTable data={data} />}

      {/* No details available (old executions) */}
      {data.filters_detail.length === 0 && !data.error_message && (
        <Text c="dimmed" size="sm">
          No detailed data available for this execution.
        </Text>
      )}
    </Stack>
  );
}

function FilterBreakdownTable({ data }: { data: ScrapeExecutionDetail }) {
  return (
    <>
      <Title order={5}>Filter Breakdown</Title>
      <Table.ScrollContainer minWidth={600}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Filter</Table.Th>
              <Table.Th>Search URL</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Found</Table.Th>
              <Table.Th>New</Table.Th>
              <Table.Th>Error</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.filters_detail.map((fd) => (
              <Table.Tr key={fd.filter_id}>
                <Table.Td>{fd.filter_name}</Table.Td>
                <Table.Td style={{ maxWidth: 300 }}>
                  <Anchor
                    href={fd.search_url}
                    target="_blank"
                    size="xs"
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {fd.search_url}
                  </Anchor>
                </Table.Td>
                <Table.Td>
                  <StatusBadge status={fd.status as "success" | "failed"} />
                </Table.Td>
                <Table.Td>{fd.apartments_found}</Table.Td>
                <Table.Td>
                  <Text c="green">{fd.new_apartments}</Text>
                </Table.Td>
                <Table.Td>
                  {fd.error && (
                    <Text c="red" size="xs">
                      {fd.error}
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </>
  );
}

function ApartmentsTable({ data }: { data: ScrapeExecutionDetail }) {
  return (
    <>
      <Title order={5}>Apartments ({data.apartments.length})</Title>
      <Table.ScrollContainer minWidth={600}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>Title</Table.Th>
              <Table.Th>Rent</Table.Th>
              <Table.Th>Rooms</Table.Th>
              <Table.Th>Size</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.apartments.map((apt) => (
              <Table.Tr key={apt.qasa_id}>
                <Table.Td>
                  <Anchor href={apt.url} target="_blank" size="sm">
                    {apt.qasa_id}
                  </Anchor>
                </Table.Td>
                <Table.Td style={{ maxWidth: 250 }}>
                  <Text size="sm" truncate>
                    {apt.title}
                  </Text>
                </Table.Td>
                <Table.Td>{apt.rent.toLocaleString()} SEK</Table.Td>
                <Table.Td>{apt.rooms ?? "-"}</Table.Td>
                <Table.Td>{apt.size_sqm ? `${apt.size_sqm} m\u00B2` : "-"}</Table.Td>
                <Table.Td>
                  {apt.is_new && (
                    <Badge color="green" size="xs">
                      NEW
                    </Badge>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </>
  );
}

function ScrapesPage() {
  const { status, page } = Route.useSearch();
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ["scrapes", { status, page }],
    queryFn: () => scrapesApi.list({ status, page, page_size: 20 }),
  });

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <Stack gap="md">
      <Title order={1}>Scrape History</Title>

      <Group gap="xs">
        {statusFilters.map((filter) => (
          <Button
            key={filter.value || "all"}
            component={Link}
            to="/scrapes"
            search={{ status: filter.value, page: 1 }}
            variant="filled"
            color={status === filter.value ? "primary" : "#0f3460"}
            radius="xl"
            size="sm"
          >
            {filter.label}
          </Button>
        ))}
      </Group>

      <Paper p="md" radius="md" withBorder style={{ borderColor: "#0f3460" }}>
        <Table.ScrollContainer minWidth={800}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Started</Table.Th>
                <Table.Th>Duration</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Found</Table.Th>
                <Table.Th>New</Table.Th>
                <Table.Th>Trigger</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data?.items.map((scrape) => {
                const isExpanded = expandedIds.has(scrape.id);
                const duration = scrape.ended_at
                  ? Math.round(
                      (new Date(scrape.ended_at).getTime() -
                        new Date(scrape.started_at).getTime()) /
                        1000
                    )
                  : null;

                return (
                  <Fragment key={scrape.id}>
                    <Table.Tr
                      onClick={() => toggleExpand(scrape.id)}
                      style={{ cursor: "pointer" }}
                      bg={isExpanded ? "rgba(15, 52, 96, 0.3)" : undefined}
                    >
                      <Table.Td>{new Date(scrape.started_at).toLocaleString()}</Table.Td>
                      <Table.Td>
                        <Text c="dimmed">{duration !== null ? `${duration}s` : "-"}</Text>
                      </Table.Td>
                      <Table.Td>
                        <StatusBadge
                          status={
                            scrape.status as "success" | "failed" | "running" | "partial_failure"
                          }
                        />
                      </Table.Td>
                      <Table.Td>{scrape.apartments_found}</Table.Td>
                      <Table.Td>
                        <Text c="green">{scrape.new_apartments}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text c="dimmed">{scrape.trigger_type}</Text>
                      </Table.Td>
                    </Table.Tr>
                    {isExpanded && (
                      <Table.Tr>
                        <Table.Td colSpan={6} p={0}>
                          <ScrapeDetailRow scrapeId={scrape.id} />
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Fragment>
                );
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        {data && data.has_more && (
          <Center mt="xl">
            <Group gap="md">
              {page > 1 && (
                <Button
                  variant="filled"
                  color="#0f3460"
                  component={Link}
                  to="/scrapes"
                  search={{ status, page: page - 1 }}
                >
                  Previous
                </Button>
              )}
              <Button
                variant="filled"
                color="#0f3460"
                component={Link}
                to="/scrapes"
                search={{ status, page: page + 1 }}
              >
                Next
              </Button>
            </Group>
          </Center>
        )}
      </Paper>
    </Stack>
  );
}
